/**
 * Wire shapes: the JSON the backends emit. The layout is generated from vms_database's
 * openapi.yaml (`npm run generate`); its items, which the spec leaves open, are declared here.
 */

import { z } from 'zod';

import { DeviceIdSchema } from './deviceId';
import { Layout as GeneratedLayoutSchema } from './generated/vms_database.zod';
import type { Write } from './writes';

export const LayoutCellAspectSchema = z.enum(['auto', '16:9', '4:3', '1:1']);
export type LayoutCellAspect = z.infer<typeof LayoutCellAspectSchema>;

export const LayoutGapSchema = z.enum(['zero', 'small', 'medium', 'large', 'xlarge']);
export type LayoutGap = z.infer<typeof LayoutGapSchema>;

/** Whether a tile keeps its video's aspect inside its grain rect or fills it. */
export const LayoutFitSchema = z.enum(['contain', 'fill']);
export type LayoutFit = z.infer<typeof LayoutFitSchema>;

/**
 * X/Y/W/H are grid cells; `level` is the scene's split depth (cells of
 * 1/2^level), which the server stores untouched. Loose: an item goes back on
 * every save, so a field a newer client wrote must survive this one.
 */
export const LayoutItemSchema = z.looseObject({
    itemId: z.string(),
    deviceId: DeviceIdSchema,
    x: z.number().int(),
    y: z.number().int(),
    w: z.number().int(),
    h: z.number().int(),
    level: z.number().int().optional(),
    fit: LayoutFitSchema.optional(),
});
export type LayoutItem = z.infer<typeof LayoutItemSchema>;

/** Loose for the same reason as its items: a layout is sent back whole. */
export const LayoutSchema = GeneratedLayoutSchema.extend({
    /** Order is meaningful: reading-order navigation follows it. */
    items: z.array(LayoutItemSchema),
}).loose();
export type Layout = z.infer<typeof LayoutSchema>;

/** The part of a layout that the scene arranges. */
export type LayoutArrangement = Pick<Layout, 'cellAspect' | 'gap' | 'items'>;

/** The layouts list as fetched. */
export interface LayoutsSnapshot {
    layouts: Layout[];
    isLoading: boolean;
    error: Error | null;
}

export enum LayoutWriteKind {
    Create = 'create',
    Update = 'update',
    Delete = 'delete',
}

/** What a write of this session asks the row to show. */
export interface LayoutWriteRequest {
    kind: LayoutWriteKind;
    name: string;
    isVideoWall: boolean;
    /** What a create from the scene sends, so a retry sends the same layout. */
    arrangement?: LayoutArrangement;
}

/** A write of this session that the list does not show yet. */
export type LayoutWrite = Write<LayoutWriteRequest>;

/** A row of the Layouts tab: a listed layout, with the write that changes it. */
export interface LayoutRow {
    id: string;
    name: string;
    isVideoWall: boolean;
    write?: LayoutWrite;
}
